import { useState, useCallback, useEffect, useRef } from "react";
import { useDebounce } from "@hooks/use-debounce";
import { useSearchQuestionResponsesMutation } from "@store/api/filters-api";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import type {
  QuestionResponsesSearchFilter,
  VariableData,
} from "@/models/Filter";
import type { CrosstabConfig } from "@store/slices/crosstab-config-slice";
import type { QuestionVariablesResponse } from "@models/Filter";

/**
 * Helper function to get the correct weight selection value for API requests
 */
const getWeightSelection = (config: CrosstabConfig): string => {
  if (config.weightOption === "default") {
    return "DATASET_DEFAULT";
  }
  if (config.weightOption === "none") {
    return "DISABLED";
  }
  if (config.weightOption === "per-segment") {
    if (
      !config.perSegmentWeights ||
      Object.keys(config.perSegmentWeights).length === 0
    ) {
      return "DATASET_DEFAULT";
    }
    return "PER_SEGMENT_WEIGHT";
  }
  // Custom weight
  if (config.customWeight) {
    return `${config.customWeight.questionId}_${config.customWeight.variableId}`;
  }
  // Fallback to disabled if custom weight is not properly configured
  return "DISABLED";
};

/**
 * Helper function to build crossColumnWeights array from perSegmentWeights
 */
const getCrossColumnWeights = (
  config: CrosstabConfig,
  columnVariableIds: Record<string, string>,
):
  | {
      surveyId: string;
      crossColumnLabel: string;
      weightVariableLabel: string;
    }[]
  | undefined => {
  if (config.weightOption !== "per-segment") {
    return undefined;
  }

  if (
    !config.perSegmentWeights ||
    Object.keys(config.perSegmentWeights).length === 0
  ) {
    return undefined;
  }

  const weights = Object.entries(config.perSegmentWeights).map(
    ([columnLabel, weight]) => ({
      surveyId: weight.surveyId,
      crossColumnLabel:
        weight.crossColumnVariableId ||
        columnVariableIds[columnLabel] ||
        columnLabel,
      weightVariableLabel: weight.weightVariableLabel,
    }),
  );

  return weights.length > 0 ? weights : undefined;
};

interface UseCrosstabFilterToggleProps {
  activeFilters: QuestionResponsesSearchFilter[];
  currentConfig: CrosstabConfig;
  columnVariableIds: Record<string, string>;
  rowVariables: QuestionVariablesResponse | undefined;
  columnVariables: QuestionVariablesResponse | undefined;
  rowNeedsFocus: boolean;
  columnNeedsFocus: boolean;
  isFocusValid: (focus: any, needsFocus: boolean) => boolean;
  onDataUpdate: (data: VariableData[]) => void;
}

export const useCrosstabFilterToggle = ({
  activeFilters,
  currentConfig,
  columnVariableIds,
  rowVariables,
  columnVariables,
  rowNeedsFocus,
  columnNeedsFocus,
  isFocusValid,
  onDataUpdate,
}: UseCrosstabFilterToggleProps) => {
  // Local state for immediate UI feedback
  const [localApplyFilters, setLocalApplyFilters] = useState(
    activeFilters.length > 0 ? true : false,
  );
  const [isTogglingFilters, setIsTogglingFilters] = useState(false);
  const isTogglingFiltersRef = useRef(false);

  // Debounce the filter toggle
  const debouncedApplyFilters = useDebounce(localApplyFilters, 500);

  // API mutation
  const [searchQuestionResponses] = useSearchQuestionResponsesMutation();

  // Sync local state with active filters
  useEffect(() => {
    if (activeFilters.length > 0) {
      setLocalApplyFilters(true);
    } else {
      setLocalApplyFilters(false);
    }
  }, [activeFilters.length]);

  // Handle filter toggle
  const handleFilterToggle = useCallback(() => {
    // Set the ref IMMEDIATELY (synchronous) before any state changes
    isTogglingFiltersRef.current = true;
    setIsTogglingFilters(true);
    const newValue = !localApplyFilters;
    setLocalApplyFilters(newValue);
  }, [localApplyFilters]);

  // Effect to refetch data when filter toggle changes (after debounce)
  // CRITICAL: Only debouncedApplyFilters should trigger this effect
  useEffect(() => {
    // Only run when we're actively toggling filters
    if (!isTogglingFilters) {
      return;
    }

    const shouldFetchData =
      currentConfig.datasetId &&
      currentConfig.rowQuestion &&
      currentConfig.columnQuestion &&
      isFocusValid(currentConfig.rowFocus, rowNeedsFocus) &&
      isFocusValid(currentConfig.columnFocus, columnNeedsFocus);

    if (!shouldFetchData) {
      isTogglingFiltersRef.current = false;
      setIsTogglingFilters(false);
      return;
    }

    // Build the request body
    const requestBody: any = {
      datasetId: currentConfig.datasetId,
      questionId: currentConfig.rowQuestion!.questionId,
      crossQuestionId: currentConfig.columnQuestion!.questionId,
      chartType: "CROSSTAB",
      weightSelection: getWeightSelection(currentConfig),
      crossColumnWeights: getCrossColumnWeights(currentConfig, columnVariableIds),
      filters:
        debouncedApplyFilters && activeFilters.length > 0
          ? activeFilters
          : undefined,
    };

    // Add focus parameters
    if (rowNeedsFocus && currentConfig.rowFocus) {
      requestBody.focusMode = currentConfig.rowFocus.focusType.toUpperCase();
      if (currentConfig.rowFocus.focusMetric === "rollup" && currentConfig.rowFocus.rollup) {
        requestBody.focusRollup = currentConfig.rowFocus.rollup;
      } else if (currentConfig.rowFocus.focusType === "variable") {
        const variable = rowVariables?.variables?.find(
          (v) =>
            v.datasetElementId === currentConfig.rowFocus?.selectedVariableId,
        );
        requestBody.focusEntity = variable?.label;
      } else {
        const value = rowVariables?.values?.find(
          (v) => v.datasetElementId === currentConfig.rowFocus?.selectedValueId,
        );
        requestBody.focusEntity =
          typeof value?.value === "number" ? value.value : Number(value?.value);
      }
    }

    if (columnNeedsFocus && currentConfig.columnFocus) {
      requestBody.crossFocusMode =
        currentConfig.columnFocus.focusType.toUpperCase();
      if (currentConfig.columnFocus.focusType === "variable") {
        const variable = columnVariables?.variables?.find(
          (v) =>
            v.datasetElementId ===
            currentConfig.columnFocus?.selectedVariableId,
        );
        requestBody.crossFocusEntity = variable?.label;
      } else {
        const value = columnVariables?.values?.find(
          (v) =>
            v.datasetElementId === currentConfig.columnFocus?.selectedValueId,
        );
        requestBody.crossFocusEntity =
          typeof value?.value === "number" ? value.value : Number(value?.value);
      }
    }

    if (currentConfig.minimumBaseSize > 0) {
      requestBody.cellBaseN = currentConfig.minimumBaseSize;
    }

    // Time period is independent — always include dates when available
    if (
      currentConfig.timePeriodSelection?.startDate &&
      currentConfig.timePeriodSelection?.endDate
    ) {
      requestBody.startDate = currentConfig.timePeriodSelection.startDate;
      requestBody.endDate = currentConfig.timePeriodSelection.endDate;
    }

    // Include statistical testing parameters if enabled AND not all_time
    if (
      currentConfig.statisticalTesting?.enabled &&
      currentConfig.timePeriodSelection?.timePeriod !== "all_time"
    ) {
      requestBody.enableStatTesting = true;
      requestBody.statTestingType = testTypeToApiFormat(
        currentConfig.statisticalTesting?.testType,
      );
      // Only include enableRowStatTesting if compareToTotal is enabled
      if (currentConfig.statisticalTesting?.compareToTotal) {
        requestBody.enableRowStatTesting = true;
        // Add referenceColumnEntity (null for overall total, crossVariableId for reference column)
        requestBody.referenceColumnEntity =
          currentConfig.statisticalTesting.referenceColumnEntity ?? null;
      }
      requestBody.pValue = significanceLevelToPValue(
        currentConfig.statisticalTesting?.significanceLevel || "90%",
      );
      requestBody.minimumSampleSize =
        currentConfig.statisticalTesting?.minimumSampleSize || 30;
    }

    // Include rolling average periodCount when enabled
    if (
      currentConfig.rollingAverage?.enabled &&
      currentConfig.rollingAverage?.windowSize
    ) {
      requestBody.periodCount = currentConfig.rollingAverage.windowSize;
    }

    console.log("🔥 FILTER TOGGLE EFFECT - Making request");
    searchQuestionResponses(requestBody)
      .unwrap()
      .then((response) => {
        console.log("✅ FILTER TOGGLE EFFECT - Request completed");
        if (response.data && Array.isArray(response.data)) {
          onDataUpdate(response.data as VariableData[]);
        }
        // Use setTimeout to ensure state updates don't trigger main effect
        setTimeout(() => {
          isTogglingFiltersRef.current = false;
          setIsTogglingFilters(false);
        }, 0);
      })
      .catch((error) => {
        console.error("❌ FILTER TOGGLE EFFECT - Request failed:", error);
        setTimeout(() => {
          isTogglingFiltersRef.current = false;
          setIsTogglingFilters(false);
        }, 0);
      });
  }, [
    // CRITICAL: Only debouncedApplyFilters should be here to trigger the effect
    debouncedApplyFilters,
    // These are stable references that won't change during toggle:
    currentConfig.datasetId,
    currentConfig.rowQuestion,
    currentConfig.columnQuestion,
    currentConfig.rowFocus,
    currentConfig.columnFocus,
    currentConfig.weightOption,
    currentConfig.perSegmentWeights,
    currentConfig.minimumBaseSize,
    currentConfig.timePeriodSelection,
    currentConfig.statisticalTesting,
    columnVariableIds,
    rowNeedsFocus,
    columnNeedsFocus,
    isFocusValid,
    rowVariables,
    columnVariables,
    activeFilters,
    searchQuestionResponses,
    onDataUpdate,
    // REMOVED: isTogglingFilters - this was causing double triggers
  ]);

  return {
    localApplyFilters,
    isTogglingFilters,
    isTogglingFiltersRef,
    handleFilterToggle,
  };
};
